'use client';

import { useCallback } from 'react';
import useSWR, { mutate } from 'swr';
import { Account } from '@shared/types/account/Account';
import { sdk } from '@/sdk';
import { Address } from '@shared/types/account/Address';
import { GetAccountActionReturn } from '@/sdk/composable-commerce-b2b/types/actions/AccountActions';
import { RegisterAccountPayload } from '@/sdk/composable-commerce-b2b/types/payloads/AccountPayloads';
import useRecaptcha from '@/lib/hooks/useRecaptcha';
import { revalidateSessionData } from '@/utils/server/session-cache/actions';
import { useMutationLock } from '@/providers/mutation-lock';

const useAccount = () => {
  const { getToken } = useRecaptcha();
  // EWM-521 — account data mutations participate in the site-wide lock.
  const { withLock } = useMutationLock();
  const getAccount = useCallback(async () => {
    const result = await sdk.composableCommerce.account.getAccount();
    return result;
  }, []);

  const result = useSWR('/action/account/getAccount', getAccount);

  const getData = useCallback(() => {
    if (result.data?.isError) return { loggedIn: false } as GetAccountActionReturn;

    return (result.data?.data ?? {}) as GetAccountActionReturn;
  }, [result]);

  const data = getData();

  const defaultShippingAddress = data.loggedIn
    ? data.account?.addresses?.find((address) => address.isDefaultShippingAddress)
    : undefined;

  const defaultBillingAddress = data.loggedIn
    ? data.account?.addresses?.find((address) => address.isDefaultBillingAddress)
    : undefined;

  const updateAccount = useCallback(
    (payload: Partial<Account>): Promise<Account> =>
      withLock(async () => {
        const res = await sdk.composableCommerce.account.updateAccount(payload);

        mutate('/action/account/getAccount');

        return res.isError ? ({} as Account) : res.data;
      }),
    [withLock],
  );

  const login = useCallback(
    async (email: string, password: string, remember?: boolean, accountNumber?: string): Promise<Account> => {
      // EWM-314 — single reCAPTCHA choke point: token minted here so every
      // consumer (login page + sign-in drawer) is protected without per-form
      // wiring. Resolves undefined in keyless degraded mode.
      const recaptchaToken = await getToken('login');
      const res = await sdk.composableCommerce.account.login({
        email: email.toLowerCase(),
        password,
        remember,
        recaptchaToken,
        accountNumber,
      });

      mutate('/action/account/getAccount');
      mutate('/action/wishlist/getWishlist');

      // EWM-280 — login changes the session; bust the per-session server cache
      // so BU/associate/active-selection re-fetch on the next server render.
      await revalidateSessionData();

      if (res.isError) {
        // EWM-179 / EWM-625 — surface the BFF's structured `errorName` on the
        // thrown Error so consumers branch on the contract (e.g.
        // account_unverified_error) rather than message-string proxies. Matches
        // the register()/requestPasswordReset() plumbing for the other typed errors.
        // EWM-804 — also forward `statusCode`. The SDK's ActionError extends
        // FetchError, which copies every property off the BFF's JSON body, and
        // handleError always writes statusCode there, so this is the one path
        // carrying the BFF's HTTP status to FE consumers of login().
        // EWM-812 — no consumer reads it today: useSignInForm was the only one,
        // and its routing is now name-only. Retained deliberately as part of the
        // thrown-error contract (it costs nothing and the status is otherwise
        // unrecoverable downstream); see useAccount.spec.tsx for its coverage.
        const { errorName, statusCode } = res.error as unknown as { errorName?: string; statusCode?: number };
        const err = new Error(res.error.message) as Error & { errorName?: string; statusCode?: number };
        if (errorName) err.errorName = errorName;
        if (typeof statusCode === 'number') err.statusCode = statusCode;
        throw err;
      }

      return res.data;
    },
    [getToken],
  );

  const logout = useCallback(async () => {
    await sdk.composableCommerce.account.logout();

    mutate('/action/account/getAccount');
    mutate('/action/wishlist/getWishlist');

    // EWM-280 — logout must wipe BU + associate + active-selection from the
    // server cache so a subsequent session doesn't read the prior dealer's data.
    await revalidateSessionData();
  }, []);

  const register = useCallback(
    async (account: Account): Promise<Account> => {
      const recaptchaToken = await getToken('register'); // EWM-314
      const res = await sdk.composableCommerce.account.register({
        ...(account as RegisterAccountPayload),
        ...(account.email ? { email: account.email.toLowerCase() } : {}),
        recaptchaToken,
      });

      if (res.isError) {
        // EWM-308 — surface the BFF's structured errorName on the thrown Error so
        // the registration surfaces branch on the contract, not message text:
        // account_email_duplicated_error / business_unit_duplicated_error (dupes)
        // + recaptcha_* (rejections). Unknown errors carry no errorName.
        const errorName = (res.error as unknown as { errorName?: string })?.errorName;
        const err = new Error(res.error.message);
        if (errorName) (err as Error & { errorName?: string }).errorName = errorName;
        throw err;
      }

      return res.data;
    },
    [getToken],
  );

  const confirm = useCallback(async (token: string): Promise<Account> => {
    const res = await sdk.composableCommerce.account.confirm({ token });

    mutate('/action/account/getAccount');

    return res.isError ? ({} as Account) : res.data;
  }, []);

  const requestPasswordReset = useCallback(
    async (email: string, accountNumber?: string): Promise<void> => {
      const recaptchaToken = await getToken('password_reset'); // EWM-314
      const res = await sdk.composableCommerce.account.requestPasswordReset({
        email: email.toLowerCase(),
        recaptchaToken,
        accountNumber,
      });
      if (res.isError) {
        // EWM-265 account-existence privacy: swallow the commercetools 404
        // (surfaced as a "not found" message) so the FE contract is genuinely
        // always-resolve. Other errors (network, rate-limit, 5xx, recaptcha
        // rejection, and EWM-550 account_number_required) bubble up so callers
        // can route them to the right UI.
        if (res.error.message.includes('not found')) return;
        const errorName = (res.error as unknown as { errorName?: string })?.errorName;
        const err = new Error(res.error.message);
        if (errorName) (err as Error & { errorName?: string }).errorName = errorName;
        throw err;
      }
    },
    [getToken],
  );

  const resetPassword = useCallback(async (token: string, newPassword: string): Promise<Account> => {
    const res = await sdk.composableCommerce.account.resetPassword({ token, newPassword });

    // EWM-664 — the standalone /reset-password page's catch block for
    // expired/invalid/reused tokens was previously dead code because this
    // swallowed every BFF error into an empty object. Throw so the
    // new-password tray can distinguish "invalid token" from a genuine
    // network failure (mirrors changePassword()/login()/register()).
    if (res.isError) throw new Error(res.error.message);

    mutate('/action/account/getAccount');

    return res.data as Account;
  }, []);

  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    const res = await sdk.composableCommerce.account.changePassword({ oldPassword, newPassword });

    if (res.isError) throw new Error(res.error.message);
    return res.data;
  }, []);

  const deleteAccount = useCallback(async (password: string) => {
    const res = await sdk.composableCommerce.account.deleteAccount({ password });

    mutate('/action/account/getAccount');

    return { success: !res.isError };
  }, []);

  const addAddress = useCallback(
    (address: Address) =>
      withLock(async () => {
        const res = await sdk.composableCommerce.account.addAddress({ address });

        mutate('/action/account/getAccount');

        return res.isError ? ({} as Account) : res.data;
      }),
    [withLock],
  );

  const updateAddress = useCallback(
    (address: Partial<Address>) =>
      withLock(async () => {
        const res = await sdk.composableCommerce.account.updateAddress({ address });

        mutate('/action/account/getAccount');

        return res.isError ? ({} as Account) : res.data;
      }),
    [withLock],
  );

  const removeAddress = useCallback(
    (addressId: string) =>
      withLock(async () => {
        const res = await sdk.composableCommerce.account.removeAddress({ address: { id: addressId } });

        mutate('/action/account/getAccount');

        return res.isError ? ({} as Account) : res.data;
      }),
    [withLock],
  );

  return {
    account: data.loggedIn ? data.account : undefined,
    isLoading: result.isLoading,
    loggedIn: !!data.loggedIn,
    defaultShippingAddress,
    defaultBillingAddress,
    login,
    logout,
    register,
    confirm,
    requestPasswordReset,
    resetPassword,
    updateAccount,
    changePassword,
    deleteAccount,
    addAddress,
    updateAddress,
    removeAddress,
  };
};

export default useAccount;
